import React, { useContext } from "react";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Pages
import Login from "./pages/Login";
import OwnerDashboard from "./pages/OwnerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import OperationsPerformanceDashboard from "./pages/OperationsPerformanceDashboard";
import GuestReviewsPage from "./pages/GuestReviewsPage";
import InboxPage from "./pages/InboxPage";
import BookingCalendar from "./pages/BookingCalendar";
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import RevenueOptimizationDashboard from "./pages/RevenueOptimizationDashboard";
import OwnerReportPrint from "./pages/OwnerReportPrint";
import OwnerBillingWorksheet from "./pages/OwnerBillingWorksheet";
import EventManagement from "./pages/EventManagement";
import PricingGuidancePage from "./pages/PricingGuidancePage";
import AvailabilityGridPage from "./pages/AvailabilityGridPage";
import StockPage from "./pages/StockPage";
import ListingPerformancePage from "./pages/ListingPerformancePage";
import BookingDetails from "./pages/BookingDetails";
import BookingsList from "./pages/BookingsList";
import GuestsPage from "./pages/GuestsPage";
import GuestDetailsPage from "./pages/GuestDetailsPage";
import UserManagement from "./pages/UserManagement";
import ActivateAccount from "./pages/ActivateAccount";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordReset from "./pages/PasswordReset";
import Tasks from "./pages/Tasks";
import GuestRequestsHubPage from "./pages/GuestRequestsHubPage";
import MaintenanceTasks from "./pages/MaintenanceTasks";
import Settings from "./pages/Settings";
import MessagingAutomationPage from "./pages/MessagingAutomationPage";
import AgentTracePage from "./pages/AgentTracePage";
import IntentPoliciesPage from "./pages/IntentPoliciesPage";
import LiveConversationsPage from "./pages/LiveConversationsPage";
import AlertsManagement from "./pages/AlertsManagement";

// Cleaning Service Pages
import ServiceDashboard from "./pages/ServiceDashboard";
import ServiceClientsPage from "./pages/ServiceClientsPage";
import ServiceClientDetail from "./pages/ServiceClientDetail";
import CreateServiceClient from "./pages/CreateServiceClient";
import CreateServiceRequest from "./pages/CreateServiceRequest";
import ServiceRequestsPage from "./pages/ServiceRequestsPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import ClientDashboard from "./pages/ClientDashboard";
// The guest door plaque. Imports nothing that touches AuthContext — see the page header.
import CheckoutTap from "./pages/CheckoutTap";
import VisitHistory from "./pages/client/VisitHistory";
import ClientLocations from "./pages/client/ClientLocations";

// Public Pages
import BookingPage from "./pages/public/BookingPage";
import CheckoutPage from "./pages/public/CheckoutPage";
import BookingConfirmation from "./pages/public/BookingConfirmation";
import BookingLookup from "./pages/public/BookingLookup";

// Security Guards
import AuthGuard from './guards/AuthGuard';
import RoleGuard from './guards/RoleGuard';
import PropertyOwnerGuard from './guards/PropertyOwnerGuard';
import CapabilityGuard from './guards/CapabilityGuard';

// Context
import { AuthContext } from "./context/AuthContext";
import { AccountProvider } from "./context/AccountContext";
import { PropertyProvider } from "./context/PropertyContext";

// Hooks
import useAuthSync from "./hooks/useAuthSync";

// Initialize token validation (runs auto-cleanup on app load)
import './utils/tokenValidator';

// Axios configuration with proper token validation
import './utils/axiosConfig';

// Config
import { roleConfig, BILLING_READ_ROLES, OWNER_ROLES, PRICING_ROLES } from './config/roleConfig';

// Styles
import './bootstrap-rtl';
import 'bootstrap/dist/css/bootstrap.rtl.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Auth Sync Wrapper - Must be inside Router to use useNavigate
const AuthSyncWrapper = ({ children }) => {
  useAuthSync(); // v3.0: Enable multi-tab authentication sync
  return children;
};

const AppSecure = () => {
  const { isAuthenticated, loading, logout, user } = useContext(AuthContext);

  // Handle user logout
  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        direction: 'rtl'
      }}>
        <div>جاري التحميل...</div>
      </div>
    );
  }

  return (
    <Router>
      <AuthSyncWrapper>
        <AccountProvider>
          <PropertyProvider>
            <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to={roleConfig[user?.role]?.defaultRoute || '/dashboard'} replace /> : <Navigate to="/login" replace />
          }
        />

        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to={roleConfig[user?.role]?.defaultRoute || '/dashboard'} replace /> : <Login />
          }
        />

        {/* Public Account Management Routes */}
        <Route
          path="/activate/:token"
          element={<ActivateAccount />}
        />

        <Route
          path="/reset-password-request"
          element={<PasswordResetRequest />}
        />

        <Route
          path="/reset-password/:token"
          element={<PasswordReset />}
        />

        {/* Public Booking Routes - No Authentication Required */}
        <Route
          path="/book/:slug"
          element={<BookingPage />}
        />

        <Route
          path="/book/:slug/checkout"
          element={<CheckoutPage />}
        />

        <Route
          path="/booking/confirmation/:confirmationCode"
          element={<BookingConfirmation />}
        />

        <Route
          path="/booking/lookup"
          element={<BookingLookup />}
        />

        <Route
          path="/booking/:confirmationCode"
          element={<BookingLookup />}
        />

        {/* Guest checkout plaque — https://checkout.hostme.sa/#/c/<token> (tasks/checkout-tap-url.md)

            PUBLIC and unauthenticated: the token is printed on a tag beside the apartment door, so
            there is no user to authenticate. NO AuthGuard, NO RoleGuard — a guest sent to /login is
            a guest who can never get back.

            🔴 THE PATH IS PRINTED ON PHYSICAL OBJECTS. `/c/:token` is frozen the moment the first
            plaque is mounted; changing it later means a site visit per apartment. Short on purpose:
            it has to fit on a small plaque and be readable if anyone ever types it.

            ⚠️ THE PRINTED URL CARRIES THE `#`, because this app is a HashRouter (line 2).
            `checkout.hostme.sa/c/<token>` reaches no route at all — it asks the web server for a file that
            does not exist. That is not a compromise: a URL fragment is never sent to the server, so
            the PAGE request reaches nginx as a bare `GET /`.

            ⚠️ THAT IS ONLY HALF THE STORY, and the half we got wrong for a day. The page then calls
            the API, and while the token was a path segment there, nginx logged it in full —
            measured on production 2026-08-12. Keeping it out of the logs took BOTH the fragment
            here and the `X-Checkout-Token` header in `services/checkoutTapApi.js`
            (tasks/checkout-token-out-of-url.md). Neither alone is sufficient. */}
        <Route
          path="/c/:token"
          element={<CheckoutTap />}
        />

        {/* Client Portal — public, token-based auth (no JWT, no AuthGuard) */}
        <Route
          path="/client/t/:token"
          element={<ClientDashboard />}
        />
        <Route
          path="/client/t/:token/visits"
          element={<VisitHistory />}
        />
        <Route
          path="/client/t/:token/locations"
          element={<ClientLocations />}
        />

        {/* Protected Routes - Require Authentication */}
        <Route
          path="/dashboard"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={OWNER_ROLES} fallbackPath="/admin/dashboard">
                <OwnerDashboard
                  handleLogout={handleLogout}
                />
              </RoleGuard>
            </AuthGuard>
          }
        />
        
        <Route
          path="/calendar"
          element={
            <AuthGuard>
              <BookingCalendar />
            </AuthGuard>
          }
        />

        {/* Properties Routes - With ownership validation */}
        <Route 
          path="/properties" 
          element={
            <AuthGuard>
              <PropertiesPage mode="list" />
            </AuthGuard>
          } 
        />

        <Route 
          path="/properties/add" 
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin']}>
                <PropertiesPage mode="add" />
              </RoleGuard>
            </AuthGuard>
          } 
        />

        <Route 
          path="/properties/:propertyId/edit" 
          element={
            <AuthGuard>
              {/* ops / ops-manager: guidebook tab only — SecurePropertiesManagement isGuidebookOnly
                  (tasks/guest-guide-sections-and-print-sheet.md G11). */}
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <PropertyOwnerGuard>
                  <PropertiesPage mode="edit" />
                </PropertyOwnerGuard>
              </RoleGuard>
            </AuthGuard>
          } 
        />
      
        <Route 
          path="/properties/:propertyId" 
          element={
            <AuthGuard>
              <PropertyOwnerGuard>
                <PropertyDetailsPage />
              </PropertyOwnerGuard>
            </AuthGuard>
          } 
        />

        {/* Booking Routes */}
        {/* Bookings Routes */}
        <Route
          path="/bookings"
          element={
            <AuthGuard>
              <BookingsList />
            </AuthGuard>
          }
        />

        {/* Tasks Route - Admin and Ops Manager Only */}
        <Route
          path="/tasks"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops-manager', 'ops', 'maintenance-supervisor']}>
                <Tasks />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Guest Requests Hub - V1.1 global triage queue. Sibling of /tasks. */}
        {/* Roles match backend READ_ROLES (CORE_STAFF + OWNER_ROLES). */}
        <Route
          path="/guest-requests"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager', 'turnKeyOwner']}>
                <GuestRequestsHubPage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Maintenance Tasks Route - Maintenance Staff Only */}
        <Route
          path="/my-tasks"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['maintenance', 'maintenance-supervisor']}>
                <MaintenanceTasks />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Cleaning Service Routes - via manageServices capability */}
        <Route
          path="/services"
          element={
            <CapabilityGuard capability="manageServices">
              <ServiceDashboard />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/clients"
          element={
            <CapabilityGuard capability="manageServices">
              <ServiceClientsPage />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/clients/new"
          element={
            <CapabilityGuard capability="manageServices">
              <CreateServiceClient />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/clients/:id"
          element={
            <CapabilityGuard capability="manageServices">
              <ServiceClientDetail />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/requests/new"
          element={
            <CapabilityGuard capability="manageServices">
              <CreateServiceRequest />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/requests"
          element={
            <CapabilityGuard capability="manageServices">
              <ServiceRequestsPage />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/invoices"
          element={
            <CapabilityGuard capability="manageServices">
              <InvoicesPage />
            </CapabilityGuard>
          }
        />

        <Route
          path="/services/invoices/:id"
          element={
            <CapabilityGuard capability="manageServices">
              <InvoiceDetailPage />
            </CapabilityGuard>
          }
        />

        {/* Messaging Automation Route - Admin Only */}
        <Route
          path="/messaging-automation"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops-manager']}>
                <MessagingAutomationPage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Settings Route - All Authenticated Users */}
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <Settings />
            </AuthGuard>
          }
        />

        {/* /messages — the OLD inbox — REMOVED 2026-08-01, superseded by /inbox (Guest Comms Inc 4).
            Its nav entry had been hidden since 2026-04-21 pending two-way sync; two-way sync is now
            what /inbox does, so the route itself goes.

            This is a SAFETY removal, not tidying. The page's composer posts to
            POST /api/v1/messages/:id/reply, which fires-and-forgets and stores no send status — so a
            Channex rejection there is invisible forever and the row still reads as delivered. Leaving
            it reachable by direct URL meant two surfaces could reply to the same guest, sharing no
            idempotency key, with only one of them recording whether the guest got it.

            The page and its components are left on disk (recoverable from git); only the route is
            gone. The backend endpoint still exists and is still guarded — retiring it is its own
            decision, tracked in tasks/guest-comms-stage1-unified-inbox.md. */}


        <Route
          path="/bookings/:bookingId"
          element={
            <AuthGuard>
              <BookingDetails />
            </AuthGuard>
          }
        />

        {/* Guest Routes — capability-guarded via roleConfig.capabilities.viewGuests */}
        <Route
          path="/guests"
          element={
            <CapabilityGuard capability="viewGuests">
              <GuestsPage />
            </CapabilityGuard>
          }
        />

        <Route
          path="/guests/:id"
          element={
            <CapabilityGuard capability="viewGuests">
              <GuestDetailsPage />
            </CapabilityGuard>
          }
        />

        {/* Alerts Management — admin + ops triage UI (v1.1.14) */}
        <Route
          path="/alerts"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <AlertsManagement />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Finance Routes */}
        <Route
          path="/finance"
          element={
            <AuthGuard>
              <RevenueOptimizationDashboard />
            </AuthGuard>
          }
        />

        {/* The owner monthly report as a printable document — no layout, no nav, so
            what reaches paper is the report and nothing else. AuthGuard only, like
            /finance: the real boundary is the API, which scopes an owner to their
            own released reports and answers 404 otherwise. */}
        <Route
          path="/finance/reports/:reportId/print"
          element={
            <AuthGuard>
              <OwnerReportPrint />
            </AuthGuard>
          }
        />

        {/* Owner commission billing — the internal accounting worksheet.

            ⚠️ An EXPLICIT RoleGuard, unlike /finance above. That route's convention is
            "the API is the boundary", which is right when everyone who can log in
            belongs in the app. It is not right here: `accountant` is an external
            contractor whose entire application is this one page, and without a guard
            they would reach a broken shell of 403s rather than a refusal. The API
            still enforces the same list — this is the outer of two gates, not the
            only one. */}
        <Route
          path="/billing"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={BILLING_READ_ROLES}>
                <OwnerBillingWorksheet />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Event Management Route - Admin, Operations, DIY Owners */}
        <Route
          path="/events"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops-manager', 'staff']}>
                <EventManagement />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Pricing Guidance Route - Ops Only.
            Path kept as /pricing-assistant: the surface behind it was rebuilt, but the
            nav entry, bookmarks and the Arabic label ops knows are unchanged. */}
        <Route
          path="/pricing-assistant"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={PRICING_ROLES}>
                <PricingGuidancePage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Admin Only Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin']}>
                <AdminDashboard />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Unified guest inbox — read-only, core staff only (Guest Comms stage 1, Inc 3).
            No frontend feature flag on purpose: CRA bakes env vars at build time, so one could not
            be flipped without a rebuild and would not be a kill switch. The backend's
            CONVERSATION_STORE_ENABLED is the switch — with it off the API 404s and the page renders
            a calm "not enabled" state. */}
        <Route
          path="/inbox"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <InboxPage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* «التقييمات والشكاوى» — our review of each departed guest + the thank-you (post-checkout review L27),
            replies to guest reviews, and guest complaints as a third tab (tasks/complaints-to-fixes.md C19) */}
        <Route
          path="/ops/reviews"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <GuestReviewsPage />
              </RoleGuard>
            </AuthGuard>
          }
        />
        {/* Operations Performance Dashboard — internal scorecard (admin / ops / ops-manager) */}
        <Route
          path="/ops/performance"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <OperationsPerformanceDashboard />
              </RoleGuard>
            </AuthGuard>
          }
        />
        {/* «التقويم» — every unit × every night on one read-only page; edits stay in the
            unit calendar (tasks/multi-unit-availability-grid.md). NOT /calendar: that path still
            mounts the legacy BookingCalendar page (unlinked, pending removal). */}
        <Route
          path="/ops/calendar"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <AvailabilityGridPage />
              </RoleGuard>
            </AuthGuard>
          }
        />
        {/* «المخزون» — the store count of guest supplies (tasks/stock.md). Ops only: ops control the
            store and record every handover (O5). */}
        <Route
          path="/ops/stock"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <StockPage />
              </RoleGuard>
            </AuthGuard>
          }
        />
        {/* «أداء الوحدات» — the weekly listing-performance record (tasks/property-performance-phase1.md).
            Core staff; thresholds and send keys are admin-only inside the page AND on the server. */}
        <Route
          path="/ops/unit-performance"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops', 'ops-manager']}>
                <ListingPerformancePage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/users"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops']}>
                <UserManagement />
              </RoleGuard>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/agent-trace"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin']}>
                <AgentTracePage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/intent-policies"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin']}>
                <IntentPoliciesPage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        <Route
          path="/admin/live-conversations"
          element={
            <AuthGuard>
              <RoleGuard allowedRoles={['admin', 'ops']}>
                <LiveConversationsPage />
              </RoleGuard>
            </AuthGuard>
          }
        />

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
          </PropertyProvider>
      </AccountProvider>
    </AuthSyncWrapper>
  </Router>
  );
};

export default AppSecure;
